import EventEmitter from 'events';
import { isEqual } from 'lodash';
import { Disposable } from 'vscode-languageserver-protocol';
import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { ConfigService } from '../config_service';
import { log } from '../log';
import { supportedLanguages } from '../../../code_suggestions_config.json';

export const BASE_SUPPORTED_CODE_SUGGESTIONS_LANGUAGES = supportedLanguages.map(
  (l) => l.languageId,
);

export const EXTENSION_TO_LANG_MAP = (() => {
  const extensionToLanguageMap = new Map<string, string>();

  supportedLanguages.forEach((language) => {
    language.fileExtensions.forEach((extension) => {
      extensionToLanguageMap.set(extension, language.languageId);
    });
  });

  return extensionToLanguageMap;
})();

export interface SupportedLanguagesUpdateParam {
  allow?: string[];
  deny?: string[];
}

export interface SupportedLanguagesService {
  isLanguageEnabled(languageId: string): boolean;
  isLanguageSupported(languageId: string): boolean;
  onLanguageChange(listener: () => void): Disposable;
}

export const SupportedLanguagesService = createInterfaceId<SupportedLanguagesService>(
  'SupportedLanguagesService',
);

@Injectable(SupportedLanguagesService, [ConfigService])
export class DefaultSupportedLanguagesService implements SupportedLanguagesService {
  #enabledLanguages: Set<string>;

  #supportedLanguages: Set<string>;

  #configService: ConfigService;

  #eventEmitter = new EventEmitter();

  constructor(configService: ConfigService) {
    this.#enabledLanguages = new Set(BASE_SUPPORTED_CODE_SUGGESTIONS_LANGUAGES);
    this.#supportedLanguages = new Set(BASE_SUPPORTED_CODE_SUGGESTIONS_LANGUAGES);
    this.#configService = configService;
    this.#configService.onConfigChange(() => this.#update());
  }

  #getConfiguredLanguages() {
    const languages: SupportedLanguagesUpdateParam = {};

    const additionalLanguages = this.#configService.get(
      'client.codeCompletion.additionalLanguages',
    );

    if (Array.isArray(additionalLanguages)) {
      languages.allow = additionalLanguages.map(this.#normalizeLanguageIdentifier);
    } else {
      log.warn(
        'Failed to parse user-configured Code Suggestions languages. Code Suggestions will still work for the default languages.',
      );
    }

    const disabledSupportedLanguages = this.#configService.get(
      'client.codeCompletion.disabledSupportedLanguages',
    );

    if (Array.isArray(disabledSupportedLanguages)) {
      languages.deny = disabledSupportedLanguages;
    } else {
      log.warn(
        'Invalid configuration for disabled Code Suggestions languages. All default languages remain enabled.',
      );
    }

    return languages;
  }

  #update() {
    const { allow = [], deny = [] } = this.#getConfiguredLanguages();
    const newSet: Set<string> = new Set(BASE_SUPPORTED_CODE_SUGGESTIONS_LANGUAGES);

    for (const language of allow) {
      newSet.add(language.trim());
    }

    for (const language of deny) {
      newSet.delete(language);
    }

    if (newSet.size === 0) {
      log.warn('All languages have been disabled for Code Suggestions.');
    }

    const previousEnabledLanguages = this.#enabledLanguages;
    this.#enabledLanguages = newSet;

    if (!isEqual(previousEnabledLanguages, this.#enabledLanguages)) {
      this.#triggerChange();
    }
  }

  isLanguageSupported(languageId: string) {
    return this.#supportedLanguages.has(languageId);
  }

  isLanguageEnabled(languageId: string) {
    return this.#enabledLanguages.has(languageId);
  }

  onLanguageChange(listener: () => void): Disposable {
    this.#eventEmitter.on('languageChange', listener);
    return { dispose: () => this.#eventEmitter.removeListener('configChange', listener) };
  }

  #triggerChange() {
    this.#eventEmitter.emit('languageChange');
  }

  #normalizeLanguageIdentifier(language: string) {
    return language.trim().toLowerCase().replace(/^\./, '');
  }

  static getLanguageForFile(filename?: string): string | undefined {
    const ext = filename?.split('.').pop();
    return EXTENSION_TO_LANG_MAP.get(`.${ext}`);
  }
}
